import { useCallback } from 'react';
import axiosInstance from '../api/axios';
import { API_ENDPOINTS } from '../api/endpoints';
import {
  Queue,
  PaginationInfo,
  CreateQueueRequest,
  UpdateQueueRequest,
  BookingWindow,
} from '../api/queueTypes';

/**
 * Queue operations — wraps the /queues endpoints. URLs build off GET_QUEUES.
 * See MODULE_API_PLAN.md §2.3.
 */
const BASE = API_ENDPOINTS.GET_QUEUES;

export interface QueuesPage {
  data: Queue[];
  pagination: PaginationInfo | null;
}

export interface PreviewResult {
  has_affected_tokens: boolean;
  total_tokens_to_cancel: number;
  affected_slots: Array<{ slot_date: string; day_of_week: string; tokens_to_cancel: number }>;
}

export const useQueues = () => {
  const listQueues = useCallback(async (page = 1, limit = 20): Promise<QueuesPage> => {
    const res = await axiosInstance.get(`${BASE}?page=${page}&limit=${limit}`);
    return { data: res.data?.data ?? [], pagination: res.data?.pagination ?? null };
  }, []);

  const getQueue = useCallback(async (id: string): Promise<Queue> => {
    const res = await axiosInstance.get(`${BASE}/${id}`);
    return res.data.data;
  }, []);

  const createQueue = useCallback(async (payload: CreateQueueRequest): Promise<Queue> => {
    const res = await axiosInstance.post(BASE, payload);
    // create returns { data: { queue } }
    return res.data?.data?.queue ?? res.data?.data;
  }, []);

  const updateQueue = useCallback(async (id: string, payload: UpdateQueueRequest): Promise<Queue> => {
    const res = await axiosInstance.patch(`${BASE}/${id}`, payload);
    return res.data.data;
  }, []);

  const duplicateQueue = useCallback(async (id: string, name: string): Promise<Queue> => {
    const res = await axiosInstance.post(`${BASE}/${id}/duplicate`, { name });
    return res.data?.data?.queue ?? res.data?.data;
  }, []);

  const previewScheduleChange = useCallback(
    async (id: string, payload: Pick<UpdateQueueRequest, 'schedule' | 'same_for_all_days'>): Promise<PreviewResult> => {
      const res = await axiosInstance.post(`${BASE}/${id}/preview-schedule-change`, payload);
      return res.data.data;
    },
    []
  );

  const getBookingWindow = useCallback(async (id: string, from?: string, to?: string): Promise<BookingWindow[]> => {
    const qs = new URLSearchParams();
    if (from) qs.set('from', from);
    if (to) qs.set('to', to);
    const res = await axiosInstance.get(`${BASE}/${id}/booking-window${qs.toString() ? `?${qs}` : ''}`);
    const data = res.data?.data;
    return Array.isArray(data) ? data : data ? [data] : [];
  }, []);

  const openBookingWindow = useCallback(async (id: string, date?: string): Promise<void> => {
    await axiosInstance.post(`${BASE}/${id}/booking-window/open`, date ? { date } : {});
  }, []);

  const closeBookingWindow = useCallback(async (id: string, date?: string): Promise<void> => {
    await axiosInstance.post(`${BASE}/${id}/booking-window/close`, date ? { date } : {});
  }, []);

  return {
    listQueues,
    getQueue,
    createQueue,
    updateQueue,
    duplicateQueue,
    previewScheduleChange,
    getBookingWindow,
    openBookingWindow,
    closeBookingWindow,
  };
};
